import type { Result } from '../editor/ai/contracts';
import type { LayerDocument } from '../editor/imageTools/document';
import { corsReadUrl } from '../services/mediaRead';
import { textLayerFile } from './textParcel';
import { paletteLayerFile } from './paletteParcel';
import { wholeLayerFiles } from './wholeParcel';

/*
 * A layered file from a chat (Tarek, 29 Oct: Split to layers "should return a file that I can only
 * open in Artwork to edit"). Octo's split keeps one JSON file beside its picture (apps-back
 * media-nodes/layer-file.ts): the canvas, the picture's own size, the fonts, every layer bottom to
 * top with its box, and its text as text when Vectorize text was on. It is the same layer document
 * Layers with Live Text lands (imageTools/document.ts), its pictures media assets by index, so the
 * editor places it with placeLayerDocument: a new artboard the picture's size, every layer on it.
 *
 * The chat links it as /layers?file=<its address>; the editor is opened on the chosen file with
 * ?layers=<its address>. Only a file in Flobi storage is read.
 */

export const LAYER_FILE_KIND = 'flobi.layers';
const STORAGE = /^https:\/\/(?:storage\.flobi\.ai|pub-[a-z0-9]+\.r2\.dev)\//i;

export interface LayerFile {
  name: string;
  size: { width: number; height: number };
  document: LayerDocument;
  pictures: Result[];
  preview: string | null;
  count: number;
  texts: number;
  /** What its artboard is called; left out, the editor names it. */
  board?: string;
  /** A whole send (wholeParcel.ts): the artboards after this one, what stays behind, and the counts across all of them. */
  whole?: { more: LayerFile[]; behind: string; pictures: number; texts: number };
}

/** The address a layered file may be read from: https, in Flobi storage, else null. */
export function layerFileAddress(raw: string | null | undefined): string | null {
  const value = String(raw ?? '').trim();
  return STORAGE.test(value) ? value : null;
}

const positive = (v: unknown): number => (Number.isFinite(Number(v)) && Number(v) > 0 ? Number(v) : 0);

/**
 * A layered file as the editor places it; throws with a sentence for the person when it is not one. A parcel another
 * app sent (cross-app pages, 29 Oct: Flow's Layerize node) carries the file inside it (`payload.file`), so the parcel's
 * own address opens like a layer file's.
 */
export function readLayerFile(raw: unknown): LayerFile {
  const outer = (raw && typeof raw === 'object' ? raw : {}) as Record<string, any>;
  // A whole board, doc or folder (a media parcel with `payload.whole`): one artboard per group.
  if (outer.kind === 'flobi.parcel' && outer.type === 'media' && outer.payload?.whole) {
    const sent = wholeLayerFiles(outer);
    if (!sent) throw Error('Artwork takes pictures and words, and there are none in what was sent.');
    const [first, ...more] = sent.files.map((file) => readLayerFile(file));
    const all = [first!, ...more];
    return { ...first!, name: String(outer.name || first!.name).slice(0, 200), preview: sent.preview, whole: { more, behind: sent.behind, pictures: all.reduce((n, f) => n + f.pictures.length, 0), texts: sent.texts } };
  }
  // Words sent from Writer (a text parcel, 29 Oct): read as text layers on a white page (textParcel.ts).
  const words = outer.kind === 'flobi.parcel' && outer.type === 'text' ? textLayerFile(outer) : null;
  if (outer.kind === 'flobi.parcel' && outer.type === 'text' && !words) throw Error('There are no words in what was sent.');
  // A brand's colours (a brand-assets parcel, 29 Oct): read as labelled chips on a white page (paletteParcel.ts).
  const colours = outer.kind === 'flobi.parcel' && outer.type === 'brand-assets' ? paletteLayerFile(outer) : null;
  if (outer.kind === 'flobi.parcel' && outer.type === 'brand-assets' && !colours) throw Error('There are no colours in what was sent.');
  const r = (words ?? colours ?? (outer.kind === 'flobi.parcel' && outer.payload?.file && typeof outer.payload.file === 'object' ? outer.payload.file : outer)) as Record<string, any>;
  if (r.kind !== LAYER_FILE_KIND) throw Error('This is not a layered file from Flobi.');
  const w = positive(r.canvas?.w), h = positive(r.canvas?.h);
  // Pictures with a media id only: a file keeps its pictures by it. A picture layer pointing at none is left out.
  const given = Array.isArray(r.pictures) ? r.pictures : [];
  const pictures: Result[] = [];
  const moved = new Map<number, number>();
  given.forEach((p: any, index: number) => {
    if (!p?.mediaAssetId || typeof p.url !== 'string') return;
    moved.set(index, pictures.push({ mediaAssetId: String(p.mediaAssetId), url: p.url, mime: String(p.mime || 'image/png'), name: String(p.name || `Layer ${index + 1}`), width: positive(p.width), height: positive(p.height) }) - 1);
  });
  const layers = (Array.isArray(r.layers) ? r.layers : []).flatMap((l: any) => {
    if (!l || !l.bbox || !['image', 'text', 'fill'].includes(l.kind)) return [];
    if (l.kind !== 'image') return [l];
    const at = moved.get(Number(l.result));
    return at === undefined ? [] : [{ ...l, result: at }];
  });
  if (!w || !h || !layers.length) throw Error('This layered file has no layers to place.');
  const sw = positive(r.size?.width), sh = positive(r.size?.height);
  return {
    name: String(r.name || 'Picture').slice(0, 200),
    size: sw && sh ? { width: Math.round(sw), height: Math.round(sh) } : { width: Math.round(w), height: Math.round(h) },
    document: { canvas: { w, h }, fonts: Array.isArray(r.fonts) ? r.fonts : [], layers },
    pictures,
    preview: typeof r.preview?.url === 'string' ? r.preview.url : null,
    count: layers.length,
    texts: layers.filter((l: any) => l.kind === 'text').length,
    ...(typeof r.board === 'string' ? { board: r.board } : {}),
  };
}

/** Reads a layered file from its address; throws with a sentence for the person. */
export async function fetchLayerFile(address: string, fetcher: typeof fetch = fetch): Promise<LayerFile> {
  const url = layerFileAddress(address);
  if (!url) throw Error('This link does not point at a layered file from Flobi.');
  let response: Response;
  try {
    response = await fetcher(corsReadUrl(url));
  } catch {
    throw Error('The layered file could not be reached. Try again.');
  }
  if (!response.ok) throw Error(response.status === 404 ? 'This layered file is gone.' : 'The layered file could not be read. Try again.');
  let body: unknown;
  try {
    body = await response.json();
  } catch {
    throw Error('This is not a layered file from Flobi.');
  }
  return readLayerFile(body);
}

/** "6 layers · 2 text · 1080 × 1350" */
export function layerFileWords(file: Pick<LayerFile, 'count' | 'texts' | 'size'>): string {
  return [`${file.count} ${file.count === 1 ? 'layer' : 'layers'}`, file.texts ? `${file.texts} text` : '', `${file.size.width} × ${file.size.height}`].filter(Boolean).join(' · ');
}
